/* eslint-disable no-debugger */
import { Fragment } from "react";
import StandardKendoReport from "@usas/ui-web/components/embeddedReports/kendoReports/StandardKendoReport";
import PopUpModal from "@usas/ui-web/components/dialogs/PopUpModal";
import useMembershipDetailReport from "./UseMembershipDetailReport";
import DatePicker from "@usas/ui-web/components/datepickers/DatePicker";
import PrimaryButton from "@usas/ui-web/components/buttons/PrimaryButton";

import OrgUnitDropdown from "../../../components/dropdowns/orgUnitDropdown/OrgUnitDropdown";
import CompetitionCategoryMultiSelect from "@usas/ui-web/components/multiSelect/competitionCategoryMultiSelect/CompetitionCategoryMultiSelect";
import ZoneMultiSelect from "@usas/ui-web/components/multiSelect/zoneMultiSelect/ZoneMultiSelect";
import LscMultiSelect from "@usas/ui-web/components/multiSelect/lscMultiSelect/LscMultiSelect";
import ClubMultiSelect from "@usas/ui-web/components/multiSelect/clubMultiSelect/ClubMultiSelect";

import Dropdown from "@usas/ui-web/components/dropdowns/Dropdown";
import Input from "@usas/ui-web/components/inputs/Input";
import YesNoSwitch from "@usas/ui-web/components/yesNoSwitch/YesNoSwitch";
import Headings from "@usas/ui-web/components/headings/Headings";
import QuestionIcon from "@usas/ui-web/components/icons/QuestionIcon";
import SecondaryButton from "@usas/ui-web/components/buttons/SecondaryButton";

import OrgGroupCombobox from "@usas/ui-web/components/comboboxes/orgGroupCombobox/OrgGroupCombobox";
import OrgRoleTreeView from "@usas/ui-web/components/tree/orgRoleTree/OrgRoleTreeView";

import style from "../shared/KendoReport.module.css";

const MembershipDetailReport = () => {
  const {
    formState,
    errorState,
    reportParameters,
    parameters,
    columns,
    modalState,
    orgUnitState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    updateFormState,
    onRunReportClicked,
    onClearForm,
    seasonYearState,
    excelColumns,
    clubFilterIds
  } = useMembershipDetailReport();

  return (
    <Fragment>
      <form onSubmit={handleSubmit} noValidate>
        <div className="row">
          <div className="col-xs-12">
            <Headings.H3>Membership Detail Report</Headings.H3>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-3">
            <OrgUnitDropdown
              label="Organizations*"
              name="orgUnitId"
              value={formState.orgUnitId}
              error={errorState.orgUnitId}
              message={errorState.orgUnitId}
              reportInfoId={326}
              onChange={(newValue, newValueLabel, e) => {
                onValueTextPairChanged(newValue, 'orgUnitId', newValueLabel, 'orgUnitName', e);
                onFormValueChanged('zones', []);
                onFormValueChanged('lscs', []);
                onFormValueChanged('clubs', []);
                onFormValueChanged('nationalJuniorTeamOnly', false);
                onFormValueChanged('nationalTeamOnly', false);
              }}
            />
          </div>
        </div>
        <div className="row">
          {(orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 12 || formState.orgUnitId === '') &&
            <>
              <div className="col-xs-12 col-sm-6 col-md-3">
                <ZoneMultiSelect
                  label={<span>Zones&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all zones." /></span>}
                  name="zones"
                  value={formState.zones}
                  error={errorState.zones}
                  message={errorState.zones}
                  onChange={(value) => {
                    onFormValueChanged('zones', value);
                    onFormValueChanged('lscs', []);
                    onFormValueChanged('clubs', []);
                  }} />
              </div>
              <div className="col-xs-12 col-sm-6 col-md-3">
                <LscMultiSelect
                  label={<span>LSCs&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all LSCs." /></span>}
                  name="lscs"
                  value={formState.lscs}
                  error={errorState.lscs}
                  message={errorState.lscs}
                  filterIds={formState.zones.map(c => c.id).join(',')}
                  onChange={(value) => {
                    onFormValueChanged('lscs', value);
                    onFormValueChanged('clubs', []);
                  }} />
              </div>
            </>
          }
          {(orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId !== 3 || formState.orgUnitId === '') &&
          <div className="col-xs-12 col-sm-6 col-md-3">
            <ClubMultiSelect
              label={<span>Clubs&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all clubs." /></span>}
              name="clubs"
              value={formState.clubs}
              error={errorState.clubs}
              message={errorState.clubs}
              filterIds={clubFilterIds}
              onChange={(value) => { onFormValueChanged('clubs', value); }} />
          </div>
          }
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-3">
            <CompetitionCategoryMultiSelect
              label={<span>Competition Categories&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all competition categories." /></span>}
              name="competitionCategories"
              value={formState.genders}
              error={errorState.genders}
              message={errorState.genders}
              onChange={(value) => { onFormValueChanged('genders', value); }}
              showMixed={false}
            />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <Dropdown
              label={<span>Season Year&nbsp;&nbsp;<QuestionIcon toolTipText="If you select a Season Year, do not enter a date range." /></span>}
              name="season"
              options={seasonYearState.options}
              value={formState.seasonId}
              error={errorState.seasonId}
              message={errorState.seasonId}
              isLoading={seasonYearState.isLoading}
              onChange={(newValue, newValueLabel, e) => {
                e && e.target && e.target.value &&
                  onValueTextPairChanged(newValue, 'seasonId', newValueLabel, 'seasonYear', e);
              }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <DatePicker
              label={<span>Members As Of&nbsp;&nbsp;<QuestionIcon toolTipText="Filters report for non-expired members at time of date input." /></span>}
              name="membersAsOfDate"
              value={formState.membersAsOfDate}
              error={errorState.membersAsOfDate}
              message={errorState.membersAsOfDate}
              countOfYears={150}
              startYearOffset={-148}
              onChange={(value) => { updateFormState('membersAsOfDate', value); }} />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-3">
            <Input
              label="Min Age"
              name="minAge"
              type="number"
              min="1"
              max="99"
              value={formState.minAge}
              error={errorState.minAge}
              message={errorState.minAge}
              onChange={(value) => { onFormValueChanged('minAge', value); }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <Input
              label="Max Age"
              name="maxAge"
              type="number"
              min="1"
              max="99"
              value={formState.maxAge}
              error={errorState.maxAge}
              message={errorState.maxAge}
              onChange={(value) => { onFormValueChanged('maxAge', value); }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <DatePicker
              label={"Course Expirations As Of"}
              name="goodStandingAsOfDate"
              value={formState.goodStandingAsOfDate}
              error={errorState.goodStandingAsOfDate}
              message={errorState.goodStandingAsOfDate}
              countOfYears={150}
              startYearOffset={-148}
              onChange={(value) => { updateFormState('goodStandingAsOfDate', value); }} />
          </div>
        </div>
        <div className="row">
          {orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 12 &&
          <>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <YesNoSwitch
              label="National Junior Team Only?"
              name="nationalJuniorTeamOnly"
              checked={formState.nationalJuniorTeamOnly === true}
              error={errorState.nationalJuniorTeamOnly}
              message={errorState.nationalJuniorTeamOnly}
              value={formState.nationalJuniorTeamOnly}
              onChange={(value) => { updateFormState('nationalJuniorTeamOnly', value); }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <YesNoSwitch
              label="National Team Only?"
              name="nationalTeamOnly"
              checked={formState.nationalTeamOnly === true}
              error={errorState.nationalTeamOnly}
              message={errorState.nationalTeamOnly}
              value={formState.nationalTeamOnly}
              onChange={(value) => { updateFormState('nationalTeamOnly', value); }} />
          </div>
          </> 
          }
          <div className="col-xs-12 col-sm-6 col-md-3">
            <YesNoSwitch
              label="Birthdate Confirmed Members Only?"
              name="birthdateConfirmedOnly"
              checked={formState.birthdateConfirmedOnly === true}
              error={errorState.birthdateConfirmedOnly}
              message={errorState.birthdateConfirmedOnly}
              value={formState.birthdateConfirmedOnly}
              onChange={(value) => { updateFormState('birthdateConfirmedOnly', value); }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <YesNoSwitch
              label="Export Detail Data to Excel"
              name="exportDetailData"
              checked={formState.exportDetailData === true}
              error={errorState.exportDetailData}
              message={errorState.exportDetailData}
              value={formState.exportDetailData}
              onChange={(value) => { updateFormState('exportDetailData', value); }} />
          </div>
          { orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 12 &&
          <div className="col-xs-12 col-sm-6 col-md-3">
            <OrgGroupCombobox
              label={<span>Group&nbsp;&nbsp;<QuestionIcon toolTipText="Filter report for group representatives." /></span>}
              name="groupName"
              valueToMatch={formState.groupName}
              error={errorState.orgGroupId}
              message={errorState.orgGroupId}
              onChange={(newValue, newValueLabel, e) => { onValueTextPairChanged(newValue, 'orgGroupId', newValueLabel, 'groupName', e); }}
              orgUnitId={formState.orgUnitId} />
          </div>
          }
          <div className="col-xs-12 col-sm-6 col-md-3">
            <OrgRoleTreeView
              label="Member Type or Member Role"
              name="orgRole"
              value={formState.orgRole}
              error={errorState.orgRole}
              message={errorState.orgRole}
              selectableLeavesOnly={false}
              singleSelect={true}
              onChange={(newValue) => { onFormValueChanged('orgRole', newValue) }}
              organizationId={1}
            />
          </div>
        </div>
        <div className="row usas-extra-top-margin usas-extra-bottom-margin">
          <div className="col-xs-12 usas-extra-top-margin">
            <PrimaryButton type="button" onClick={(e) => onRunReportClicked(e)}>Run</PrimaryButton>&nbsp;
            <SecondaryButton type="button" onClick={() => onClearForm()}>Clear Form</SecondaryButton>
          </div>
        </div>
      </form>
      <PopUpModal
        className={style.reportModalTop}
        widthPct={98}
        maxWidth={2050}
        displayPopUp={modalState.displayPopUp}
        onModalCanceled={onModalCancelClicked}>
        <StandardKendoReport
          reportInfoId={326}
          reportParameters={reportParameters}
          title="Membership Detail Report"
          parameters={parameters}
          columns={columns}
          usePagination={false}
          pageSize={15}
          enablePdfExport={true}
          enableExcelExport={true}
          gridHeight="750px"
          detailArrayName="roleDetails"
          detailReportTitle="Role Details"
          detailGridHeight="160px"
          excelColumns={excelColumns}
          exportDetailData={formState.exportDetailData}
        />
      </PopUpModal>
    </Fragment>
  );
}

export default MembershipDetailReport;